import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { WithBackend } from '../../../backend/BackendProvider'
import {
  COACH_DASHBOARD_LOAD_CATEGORIES,
  timeSeries as timeSeriesNames,
  datePickerDefaultOptions
} from '../../../common/Constants'
import TableInteractive from './TableInteractive'
import { WithFlashMessages } from 'components/flashmessages/FlashMessageProvider'
import { WithAppsignal } from 'components/appsignal/AppsignalProvider'
import GenericPlaceholder from 'components/common/GenericPlaceholder'
import Routes from 'components/common/Routes'
import _ from 'lodash'
import SelectDropdown from 'components/common/SelectDropdown'
import {
  createAthleteRowEntry,
  addFirstDatasetOfEachType,
  fetchUserData,
  willAddAcwr,
  prepareTrainingLogData
} from './CoachDashboardHelper'
import CoachLoadChart from './CoachLoadChart'
import ParameterKnowledgeBar from './ParameterKnowledgeBar'
import SpinnerWrapper from 'components/common/SpinnerWrapper'
import useDeepCompareEffect from 'use-deep-compare-effect'
import DatePicker from 'react-datepicker'
import classNames from 'classnames'
import I18n from 'i18n'
import {
  isDailyQuestionnaire,
  isQuestionnaire,
  isTrainingLog,
  getWellnessScore,
  readTrainingQuestionnaire
} from '../../../common/utils/QuestionnaireUtils'
import { getTrainingLoad } from '../../../common/Calculations'
import { ms2kmh } from '../../../common/Units'
import CoachLoadDaySlider from '../questionnaire/common/CoachLoadDaySlider'
import moment from 'moment/moment'
import { useTrainingSchedules } from '../../../training/context/TrainingSchedulesContext'
// TO DO: After updating to react router v6, we can use useNavigate.
import { useHistory } from 'react-router-dom'

const LOAD_TAB_WINDOW_DAYS = 27

const CoachLoadTab = (props) => {
  const { questionnaireMetrics, loadTitles } = props
  const history = useHistory()
  const { getSchedulesForAthleteAndDay, isLoading: schedulesLoading } = useTrainingSchedules()

  // Created unique dropdown options with source suffixes to differentiate metrics with same IDs
  const dropdownOptions = [
    ...Object.entries(loadTitles).map(([value, name]) => ({
      value: `${value}_daily`, // Unique value for dropdown selection
      originalValue: value, // Original metric key for data lookup
      name,
      source: 'daily', // Source identifier for correct data filtering
      uniqueId: `${value}_daily`
    })),
    ...Object.entries(questionnaireMetrics).map(([value, name]) => ({
      value: `${value}_training`, // Unique value for dropdown selection
      originalValue: value, // Original metric key for data lookup
      name,
      source: 'training', // Source identifier for correct data filtering
      uniqueId: `${value}_training`
    })),
    { value: 'wellbeingScore', originalValue: 'wellbeingScore', name: I18n.t('components.dashboards.coach_individual.load.wellbeing_score'), source: 'wellbeing', uniqueId: 'wellbeingScore_wellbeing' },
    { value: 'srpe', originalValue: 'srpe', name: I18n.t('components.dashboards.coach_individual.load.srpe'), source: 'srpe', uniqueId: 'srpe_training' },
    { value: 'expectedRpe', originalValue: 'expected_rpe', name: I18n.t('components.dashboards.coach_individual.load.expected_rpe'), source: 'scheduled', uniqueId: 'expectedRpe_scheduled' }
  ]
  // Passing objects instead of strings to allow differentiation between metrics
  const [selectedMetric1, setSelectedMetric1] = useState(
    dropdownOptions[0] || null
  )
  const [selectedMetric2, setSelectedMetric2] = useState(
    dropdownOptions[1] || null
  )

  const tableId = 'load-table'

  const today = moment().endOf('day').toDate()
  const defaultStartDate = moment(today)
    .subtract(LOAD_TAB_WINDOW_DAYS, 'days')
    .startOf('day')
    .toDate()

  const [startDay, setStartDay] = useState(defaultStartDate)
  const [endDay, setEndDay] = useState(today)
  const [windowStartDate, setWindowStartDate] = useState(defaultStartDate)

  // Keep derivation, but end-of-day to include the full end date
  const windowEndDate = useMemo(
    () => moment(windowStartDate).add(LOAD_TAB_WINDOW_DAYS, 'days').endOf('day').toDate(),
    [windowStartDate]
  )
  const [athleteDatasets, setAthleteDatasets] = useState([])
  const [athleteLatestData, setAthleteLatestData] = useState(undefined)
  const [dataIsFetched, setDataIsFetched] = useState(false)
  const isFirstRender = useRef(true)

  // caching the athlete id to prevent expected rpe data loss
  const [cachedAthleteId, setCachedAthleteId] = useState(null)

  const {
    athleteProfile,
    titles,
    groupId,
    backend,
    flashMessages,
    appsignal,
    selectedHomeTabDate
  } = props

  // Update startDay and endDay based on selectedHomeTabDate (backward-looking 28-day window)
  useEffect(() => {
    if (selectedHomeTabDate) {
      const end = moment(selectedHomeTabDate).endOf('day')
      const start = end.clone().subtract(LOAD_TAB_WINDOW_DAYS, 'days').startOf('day')
      setWindowStartDate(start.toDate())
      setStartDay(start.toDate())
      setEndDay(end.toDate())
    }
  }, [selectedHomeTabDate])

  // Cache athlete ID when available to prevent Expected RPE data loss
  useEffect(() => {
    if (athleteProfile?.id && athleteProfile.id !== cachedAthleteId) {
      setCachedAthleteId(athleteProfile.id)
    }
  }, [athleteProfile?.id, cachedAthleteId])

  const reliableAthleteId = athleteProfile?.id || cachedAthleteId

  const requiredTimeSeries = [
    timeSeriesNames.SPEED,
    timeSeriesNames.ELEVATION,
    timeSeriesNames.DISTANCE,
    timeSeriesNames.CADENCE,
    timeSeriesNames.POWER,
    timeSeriesNames.HR
  ]

  const additionalSummaryFields = {
    speed_load: (summary) =>
      getTrainingLoad(
        ms2kmh(summary?.time_series_summaries?.speed?.mean),
        summary?.duration
      ),
    power_load: (summary) =>
      getTrainingLoad(
        summary?.time_series_summaries?.power?.mean,
        summary?.duration
      )
  }
  requiredTimeSeries.forEach((metric) => {
    additionalSummaryFields[`avg_${metric}`] = (summary) =>
      summary?.time_series_summaries?.[metric]?.mean
    additionalSummaryFields[`max_${metric}`] = (summary) =>
      summary?.time_series_summaries?.[metric]?.max
  })

  // When user picks a START date -> forward 28 days
  const handleStartChange = (selectedDate) => {
    const start = moment(selectedDate).startOf('day')
    const end = start.clone().add(LOAD_TAB_WINDOW_DAYS, 'days').endOf('day')

    setWindowStartDate(start.toDate())
    setStartDay(start.toDate())
    setEndDay(end.toDate())
  }

  // When user picks an END date -> back 28 days
  const handleEndChange = (selectedDate) => {
    const end = moment(selectedDate).endOf('day')
    const start = end.clone().subtract(LOAD_TAB_WINDOW_DAYS, 'days').startOf('day')

    setWindowStartDate(start.toDate())
    setStartDay(start.toDate())
    setEndDay(end.toDate())
  }

  const smartSetDateRange = useCallback((startDate, endDate) => {
    const smartStartDate = startDate.clone().startOf('day')
    const smartEndDate = endDate.clone().endOf('day')
    setStartDay(smartStartDate.toDate())
    setEndDay(smartEndDate.toDate())
  }, [])

  const fetchAthleteData = (athleteId, groupId, startDay, endDay) => {
    const start = moment(startDay).startOf('day')
    const end = moment(endDay).endOf('day')
    return fetchUserData(backend, groupId, athleteId, start, end)
  }

  const questionnaires = _.filter(athleteDatasets, isQuestionnaire)
  const dailyQuestionnaires = _.filter(questionnaires, (d) =>
    isDailyQuestionnaire(d?.structured_data_objects?.[0])
  )
  const trainingLogs = _.filter(questionnaires, (d) =>
    isTrainingLog(d?.structured_data_objects?.[0])
  )

  // Debounced data fetching to improve performance during slider interactions
  // It is a lot faster to fetch data after the user stops interacting with the slider
  const debouncedFetchData = useMemo(
    () => _.debounce((athleteId, groupId, startDay, endDay) => {
      setDataIsFetched(false)
      fetchAthleteData(athleteId, groupId, startDay, endDay)
        .then((datasets) => setAthleteDatasets(datasets))
        .catch((err) => {
          console.error('Error fetching athlete data:', err)
          appsignal.sendError(err.toJSON?.() || err)
          flashMessages.push(
            I18n.t(
              'flashmessages.general.error_retrieving_data'
            ),
            flashMessages.duration.SHORT,
            flashMessages.levels.ERROR
          )
        })
    }, 300), // Wait 300ms after user stops interacting
    [backend, flashMessages, appsignal]
  )

  useEffect(() => {
    if (reliableAthleteId && groupId) {
      debouncedFetchData(reliableAthleteId, groupId, startDay, endDay)
    }
  }, [reliableAthleteId, groupId, startDay, endDay, debouncedFetchData])

  useEffect(() => {
    if (!isFirstRender.current) {
      setDataIsFetched(true)
    } else {
      isFirstRender.current = false
    }
  }, [athleteDatasets])

  useDeepCompareEffect(() => {
    const latestDatasets = {}
    addFirstDatasetOfEachType(latestDatasets, athleteDatasets)

    willAddAcwr(latestDatasets, backend, reliableAthleteId, endDay)
      .then(() => setAthleteLatestData(latestDatasets))
  }, [reliableAthleteId, endDay, athleteDatasets])

  const extractMetricTimeseries = (questionnaires, metricKey) => {
    // Return individual entries directly, no grouping by date
    const individualEntries = []

    const calculateHours = (row) => {
      const hours = row?.values?.v2_uren
      const minutes = row?.values?.v2_minuten

      if ((hours !== undefined && hours !== null) || (minutes !== undefined && minutes !== null)) {
        const hoursNum = parseInt(hours) || 0
        const minutesNum = parseInt(minutes) || 0
        // I normalized the minutes to percentage of an hour.
        return hoursNum + (Math.round(minutesNum / 6) * 6) / 60
      }
      return null // Return null for invalid data so it gets filtered out
    }

    questionnaires.forEach((entry) => {
      const row = entry?.structured_data_objects?.[0]?.data_rows?.[0]
      const date = moment(entry?.metadatum?.event_start)

      const value = metricKey === 'v2' ? calculateHours(row) : row?.values?.[metricKey]

      if (date.isValid() && value != null && !isNaN(parseFloat(value))) {
        individualEntries.push({
          date: date.toDate(),
          value: parseFloat(value),
          count: 1,
          entries: [{
            time: date.format('HH:mm'),
            value: parseFloat(value),
            entry: entry
          }]
        })
      }
    })

    return individualEntries.sort((a, b) => a.date - b.date)
  }
  const extractWellbeingScoreSeries = (dailyQuestionnaires) => {
    // Return individual entries directly, no grouping by date
    const individualEntries = []

    dailyQuestionnaires.forEach((entry) => {
      const row = entry?.structured_data_objects?.[0]?.data_rows?.[0]
      const date = moment(entry?.metadatum?.event_start)
      if (!row || !date.isValid()) return

      const score = getWellnessScore(row.values)
      if (score == null || isNaN(score)) return

      individualEntries.push({
        date: date.toDate(),
        value: score,
        count: 1,
        entries: [{
          time: date.format('HH:mm'),
          value: score,
          entry: entry
        }]
      })
    })

    return individualEntries.sort((a, b) => a.date - b.date)
  }

  const extractScheduledRpeSeries = () => {
    if (!reliableAthleteId) {
      return []
    }

    const individualEntries = []

    if (schedulesLoading) {
      return individualEntries
    }

    const startDate = moment(startDay).startOf('day')
    const endDate = moment(endDay).endOf('day')
    const currentDate = startDate.clone()

    while (currentDate.isSameOrBefore(endDate)) {
      try {
        const dayString = currentDate.format('YYYY-MM-DD')
        const scheduledTrainings = getSchedulesForAthleteAndDay(reliableAthleteId, dayString) || []

        scheduledTrainings.forEach((schedule) => {
          const expectedRpe = schedule?.expected_rpe
          if (expectedRpe != null && !isNaN(parseFloat(expectedRpe))) {
            individualEntries.push({
              date: currentDate.toDate(),
              value: parseFloat(expectedRpe),
              count: 1,
              entries: [{
                time: moment(schedule.start_date).format('HH:mm'),
                value: parseFloat(expectedRpe),
                entry: schedule
              }]
            })
          }
        })
      } catch (error) {
        console.warn('Error getting scheduled trainings for', currentDate.format('YYYY-MM-DD'), error)
        appsignal.sendError(new Error(`extractScheduledRpeSeries: Error getting scheduled trainings for ${currentDate.format('YYYY-MM-DD')}: ${error.message}`))
      }
      currentDate.add(1, 'day')
    }

    return individualEntries.sort((a, b) => a.date - b.date)
  }

  const extractSrpeSeries = (trainingLogs) => {
    const individualEntries = []

    trainingLogs.forEach((entry) => {
      const row = entry?.structured_data_objects?.[0]?.data_rows?.[0]

      const rawEventStart = entry?.metadatum?.event_start
      const date = moment.parseZone(rawEventStart)

      if (!row || !date.isValid()) return

      const trainingData = readTrainingQuestionnaire(row?.values)
      const preparedData = prepareTrainingLogData(trainingData)

      if (preparedData?.srpe != null && !isNaN(preparedData.srpe)) {
        const timeFormatted = date.format('HH:mm')

        individualEntries.push({
          date: date.toDate(),
          value: preparedData.srpe,
          count: 1,
          entries: [{
            time: timeFormatted,
            value: preparedData.srpe,
            rpe: preparedData.rpe,
            duration: preparedData.duration,
            entry: entry
          }]
        })
      }
    })

    return individualEntries.sort((a, b) => a.date - b.date)
  }

  const getMetricSeries = (selectedMetric) => {
    if (!selectedMetric) return []

    if (selectedMetric.originalValue === 'wellbeingScore') {
      return extractWellbeingScoreSeries(dailyQuestionnaires)
    }

    if (selectedMetric.originalValue === 'expected_rpe') {
      return extractScheduledRpeSeries()
    }

    if (selectedMetric.originalValue === 'srpe') {
      return extractSrpeSeries(trainingLogs)
    }

    if (selectedMetric.source === 'training') {
      return extractMetricTimeseries(trainingLogs, selectedMetric.originalValue)
    }

    return extractMetricTimeseries(
      dailyQuestionnaires,
      selectedMetric.originalValue
    )
  }

  const metric1Series = getMetricSeries(selectedMetric1)
  const metric2Series = getMetricSeries(selectedMetric2)

  // Find correct option object by unique value to preserve all metric info
  const handleMetricChange = (index) => (e) => {
    const selectedValue = e?.target?.value || e
    const option = dropdownOptions.find((opt) => opt.value === selectedValue)
    if (option) {
      if (index === 0) setSelectedMetric1(option)
      if (index === 1) setSelectedMetric2(option)
    }
  }

  const wellbeingMetrics = getWellnessScore(dailyQuestionnaires)
  const sanitizeSeries = (series) =>
    (series || [])
      .map((d) => {
        const x = d?.date instanceof Date ? d.date.getTime() : null
        const y = typeof d?.value === 'number' ? d.value : null
        const count = d?.count || 1
        const entries = d?.entries || []
        return x != null && y != null ? { x, y, count, entries, originalData: d } : null
      })
      .filter(Boolean)

  const metric1Data = sanitizeSeries(metric1Series)
  const metric2Data = sanitizeSeries(metric2Series)

  const getChartType = (metric) => {
    const metricValue = metric?.originalValue || metric?.value
    const metricName = metric?.name?.toLowerCase() || ''

    const isRpeMetric = metricValue === 'srpe' ||
                       metricValue === 'expected_rpe' ||
                       metricValue === 'rpe' ||
                       metricName.includes('rpe')

    return isRpeMetric ? 'column' : 'line'
  }

  const metric1ChartType = getChartType(selectedMetric1)
  const metric2ChartType = getChartType(selectedMetric2)

  // Make sure array order is retained in object by making array index id (instead of athleteId).
  const entry = {}
  if (
    athleteProfile &&
    (athleteLatestData?.activity ||
      athleteLatestData?.trainingLog ||
      athleteLatestData?.dailyQuestionnaire)
  ) {
    entry[reliableAthleteId] = createAthleteRowEntry(athleteProfile, athleteLatestData)
  }
  return (
    <>

      <div className='row'>
        <div className='col s12'>
          <TableInteractive
            categories={COACH_DASHBOARD_LOAD_CATEGORIES}
            titles={titles}
            id={tableId}
            entries={entry}
          />
        </div>
      </div>

      <div className='row center-align'>
        <div className='col s12 m6'>
          <SelectDropdown
            id={`header-dropdown-${tableId}-0`}
            contentId={0}
            content={dropdownOptions}
            className='header-dropdown'
            defaultValue={selectedMetric1?.value}
            onChange={handleMetricChange(0)}
          />
        </div>
        <div className='col s12 m6'>
          <SelectDropdown
            id={`header-dropdown-${tableId}-1`}
            contentId={1}
            content={dropdownOptions}
            className='header-dropdown'
            defaultValue={selectedMetric2?.value}
            onChange={handleMetricChange(1)}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col s12'>
          <div className='chart-container'>
            {reliableAthleteId
              ? (
                <SpinnerWrapper ready={dataIsFetched}>
                  <CoachLoadChart
                    dailyQuestionnaires={dailyQuestionnaires}
                    metric1={selectedMetric1}
                    metric2={selectedMetric2}
                    metric1Data={metric1Data}
                    metric2Data={metric2Data}
                    metric1Label={selectedMetric1?.name}
                    metric2Label={selectedMetric2?.name}
                    metric1ChartType={metric1ChartType}
                    metric2ChartType={metric2ChartType}
                    wellbeingMetrics={wellbeingMetrics}
                  />
                </SpinnerWrapper>
                )
              : (
                <GenericPlaceholder
                  title={I18n.t('components.dashboards.coach_individual.load.no_athlete_selected_title')}
                />
                )}
          </div>
        </div>
      </div>
      <ParameterKnowledgeBar metric1={selectedMetric1} metric2={selectedMetric2} />
      <div className='row'>
        <div className='col s6 input-field'>
          <DatePicker
            id='load-tab-start-picker'
            {...datePickerDefaultOptions()}
            selected={startDay}
            selectStart
            onChange={handleStartChange}
          />
          <label
            htmlFor='load-tab-start-picker'
            className={classNames({ active: startDay })}
          >
            {I18n.t(
              'components.dashboards.coach_individual.training.start_date'
            )}
          </label>
        </div>
        <div className='col s6 input-field'>
          <DatePicker
            id='load-tab-end-picker'
            {...datePickerDefaultOptions()}
            selected={endDay}
            selectEnd
            onChange={handleEndChange}
          />
          <label
            htmlFor='load-tab-end-picker'
            className={classNames({ active: endDay })}
          >
            {I18n.t(
              'components.dashboards.coach_individual.training.end_date'
            )}
          </label>
        </div>
        <CoachLoadDaySlider
          minDate={moment(windowStartDate)}
          maxDate={moment(windowEndDate)}
          sliderStartDate={moment(startDay)}
          sliderEndDate={moment(endDay)}
          smartSetDateRange={smartSetDateRange}
        />
      </div>

      <div className='row dashboard-tiles'>
        <div className='col s12 m6'>
          <a
            href={Routes.network.groups.indexFn(groupId, 'overview')}
            className='card dashboard-tile tile-training'
            // Added onclick to stimulate a click, for the content to show in the tab.
            onClick={(e) => {
              e.preventDefault()
              history.push(`${Routes.network.groups.indexFn(groupId)}?tab=overview`)
            }}
          >
            <div className='card-content center'>
              <span className='card-title'>
                <GenericPlaceholder
                  title={I18n.t(
                    'components.dashboards.coach_individual.load.training.title'
                  )}
                  message={I18n.t(
                    'components.dashboards.coach_individual.load.training.placeholder'
                  )}
                />
              </span>
            </div>
          </a>
        </div>
        <div className='col s12 m6'>
          <a
            href={Routes.analysis.questionnaire.weeklyFn(
              props.athleteProfile?.id,
              'wekelijks'
            )}
            className='card dashboard-tile tile-wellness'
          >
            <div className='card-content center'>
              <span className='card-title'>
                <GenericPlaceholder
                  title={I18n.t(
                    'components.dashboards.coach_individual.load.wellness.title'
                  )}
                  message={I18n.t(
                    'components.dashboards.coach_individual.load.wellness.placeholder'
                  )}
                />
              </span>
            </div>
          </a>
        </div>
        <div className='col s12 m6'>
          <a
            href={Routes.analysis.questionnaire.indexFn(
              props.athleteProfile?.id,
              'lichamelijke_klachten'
            )}
            className='card dashboard-tile tile-complaints'
          >
            <div className='card-content center'>
              <span className='card-title'>
                <GenericPlaceholder
                  title={I18n.t(
                    'components.dashboards.coach_individual.load.complaints.title'
                  )}
                  message={I18n.t(
                    'components.dashboards.coach_individual.load.complaints.placeholder'
                  )}
                />
              </span>
            </div>
          </a>
        </div>
        <div className='col s12 m6'>
          <a
            href={Routes.network.groups.indexFn(props.groupId, 'overview')}
            className='card dashboard-tile tile-group'
          >
            <div className='card-content center'>
              <span className='card-title'>
                <GenericPlaceholder
                  title={I18n.t(
                    'components.dashboards.coach_individual.load.group.title'
                  )}
                  message={I18n.t(
                    'components.dashboards.coach_individual.load.group.placeholder'
                  )}
                />
              </span>
            </div>
          </a>
        </div>
      </div>
    </>
  )
}

export default WithAppsignal(WithFlashMessages(WithBackend(CoachLoadTab)))
